import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Flex variant styles using Class Variance Authority (CVA)
 *
 * Defines direction, gap, wrap, alignment, and justification options for flex layouts.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Direction (base and responsive at md, lg breakpoints)
 * - column: Vertical stacking (replaces Stack)
 * - row: Horizontal layout (replaces Cluster)
 *
 * ## Gap Options (spacing between children)
 * - xxs through 3xl matching design tokens
 *
 * ## Wrap Options
 * - wrap: Items wrap to next line
 * - nowrap: Items stay on single line
 *
 * ## Alignment Options (cross-axis)
 * - start, center, end, stretch
 *
 * ## Justify Options (main-axis)
 * - start, center, end, between
 */
export const flexVariants = cva('flex', {
  variants: {
    direction: {
      column: 'flex-col',
      row: 'flex-row',
    },
    'direction-md': {
      column: 'md:flex-col',
      row: 'md:flex-row',
    },
    'direction-lg': {
      column: 'lg:flex-col',
      row: 'lg:flex-row',
    },
    gap: {
      none: 'g-none',
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
      '2xl': 'gap-2xl',
      '3xl': 'gap-3xl',
    },
    wrap: {
      wrap: 'flex-wrap',
      nowrap: 'flex-nowrap',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
  },
  defaultVariants: {
    direction: 'column',
    gap: 'md',
    wrap: 'nowrap',
    align: 'stretch',
    justify: 'start',
  },
});

export type FlexVariantProps = VariantProps<typeof flexVariants>;
